<script setup>
import { computed, ref } from "vue";
import IconPlusSquare from "@/components/icons/IconPlusSquare.vue";
import { fetchAll } from "@/api/interaction_tags"
import { useUserStore } from "@/stores/user";

const userStore = useUserStore();

const emit = defineEmits(["add-tag", "create-tag"]);

const searchQuery = ref("");
const dropdownVisible = ref(false);

const props = defineProps({
  threadTags: {
    type: Array,
    default: [],
  },
});

// tag options should be requested in this component
const organizationTags = ref([])
const fetchOrganizationTags = async () => {
  const organzationId = userStore.authUserOrganizationId
  try {
    const { data } = await fetchAll(organzationId)
    const organizationTagsObj = data.find(orgTags => orgTags.organization_id === organzationId) || {tags: []}
    const lowerCaseOrganizationTags = organizationTagsObj.tags.map(tag => tag.toLowerCase())

    // filter out the thread tags
    organizationTags.value = lowerCaseOrganizationTags.filter(tag => !_isTagInThreadTags(tag))
  } catch(err) {
    console.error("Error fetching organizationTags: ", err)
  }
}

const _isTagInThreadTags = (tag) => {
  const lowerCaseThreadTags = props.threadTags.map(tag => tag.toLowerCase())
  return lowerCaseThreadTags.includes(tag)
}

const hideDropdown = () => {
  dropdownVisible.value = false;
};

const createNewTag = (newTag) => {
  emit("add-tag", newTag);
  searchQuery.value = "";
  hideDropdown();
};

const AddToOrganizationTagsAndThreadTags = (newTag) => {
  const organzationId = userStore.authUserOrganizationId
  emit("create-tag", newTag, organzationId)
  searchQuery.value = "";
  hideDropdown();
}

const filteredOptions = computed(() => {
  // clean search
  const sanitizedSearchQuery = searchQuery.value?.trim()

  return organizationTags.value.filter((tag) =>
    tag.includes(sanitizedSearchQuery.toLowerCase()),
  );
});

// fetch organizationTags
watch(() => props.threadTags, async () => {
  console.log("fetching tags")
  await fetchOrganizationTags()
}, {immediate: true})
</script>

<template>
  <BDropdown
    class="tags-dropdown"
    start
    no-caret
    auto-close="outside"
    v-model="dropdownVisible"
  >
    <template #button-content>
      <IconPlusSquare />
    </template>

    <div class="p-2">
      <div>
        <text-input
          name="add-tags"
          v-model="searchQuery"
          :placeholder="
            !filteredOptions.length && !searchQuery
              ? 'Create tag...'
              : 'Search tags...'
          "
          type="search"
        />
      </div>

      <div class="list" style="padding: 5px">
        <div
          v-for="option in filteredOptions"
          :key="option"
          @click="createNewTag(option)"
          class="list-item"
        >
          <span class="text-neutral-400">
            {{ option }}
          </span>
        </div>
        <div
          @click="AddToOrganizationTagsAndThreadTags(searchQuery)"
          v-if="searchQuery && !filteredOptions.length"
          class="list-item text-neutral-400 create-new-item"
        >
          Create tag “{{ searchQuery }}”
        </div>
        <div
          v-if="!filteredOptions.length && !searchQuery"
          class="text-neutral-400"
        >
          No Tags yet
        </div>
      </div>
    </div>
  </BDropdown>
</template>

<style scoped lang="scss">
.tags-dropdown {
  :deep(.btn) {
    padding: 4px;
    border-radius: 4px;
    border: 1px dashed $neutral-200;
    background: white;
    display: flex;
  }
}

.create-new-item {
  font-weight: bold;
}

.list {
  max-height: 240px;
  overflow-y: auto;
}

.list-item {
  padding: 12px 5px;
  border-radius: 5px;
  position: relative;

  &:hover {
    background-color: $neutral-50;
  }
}

:deep {
  .dropdown-menu {
    width: 280px;
  }
}
</style>
